import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import { formatNumber } from '@dcp/shared/utils/formatters'
import { useOrganization } from '@/contexts/OrganizationContext'
import { createTenantDb } from '@/lib/db-tenant'
import { invalidateAfterProjectionImportDelete } from '@/lib/projection-invalidation'

/** `delete_projection_import` raises this (HTTP 404) when the import is gone or not a completed projection import. */
const NOT_DELETABLE = 'PT404'

export interface DeleteProjectionImportInput {
  fileImportId: string
  /** Shown in the success toast. */
  fileName: string
}

/**
 * Delete a completed projection import together with every projection it
 * created. Toasts the outcome itself, so callers only decide what to do with
 * the dialog: the toast must survive the Import History row (and the dialog it
 * owns) unmounting once the refreshed list no longer contains the import.
 */
export function useDeleteProjectionImport() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation<number, Error, DeleteProjectionImportInput>({
    mutationFn: async ({ fileImportId }) => {
      if (!orgId) throw new Error('No organization selected')
      const { data, error } = await createTenantDb(orgId).fileImports.deleteProjectionImport(fileImportId)
      if (error) {
        if (error.code === NOT_DELETABLE) {
          // Usually deleted in another tab: the list and everything its
          // projections fed are out of date here, so refresh as after a delete.
          invalidateAfterProjectionImportDelete(queryClient, orgId, fileImportId)
          throw new Error('This import can no longer be deleted.')
        }
        throw new Error(error.message)
      }
      return data
    },
    onSuccess: (deleted, { fileImportId, fileName }) => {
      if (orgId) invalidateAfterProjectionImportDelete(queryClient, orgId, fileImportId)
      toast.success(`Deleted ${fileName} and ${formatNumber(deleted)} projection${deleted === 1 ? '' : 's'}.`)
    },
    onError: (error) => toast.error(error.message),
  })
}
